import { useEffect, useMemo, useState } from 'react'

import { useWindowWidth } from '@awazevr/design-system.utils.react'
import { readSmartSearchPrompt } from '@awazevr/mfe-common.compositions.search-experience'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import { AggregationFeatures } from '@awazevr/mfe-common.types-and-mocks.one-search-client'

import { OPTIMIZELY_FLAGS_ENUM } from '@/constants'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { breakpoints } from '@/utils/breakpoints'

import {
  buildSelectedFilters,
  containsYear2025,
  containsYear2027,
  getPopularDestinations,
  processFeaturesAndSiteFeatures,
} from './Filters.utils'
import {
  SelectedFilterChips,
  shouldShowSelectedFilterChips,
} from './SelectedFilters'

import styles from './SmartSearchAppliedFiltersRow.module.scss'

export const SmartSearchAppliedFiltersRow = (): JSX.Element | null => {
  const dispatch = useSearchContextDispatch()
  const { formatMessage } = useIntlContext()
  const { brand } = useApplicationConfig()
  const optiContext = useOptimizelyDecisionsContext()
  const width = useWindowWidth()
  const [hasSmartSearchPrompt, setHasSmartSearchPrompt] = useState(false)

  const {
    aggregations = null,
    searchCriteria = {},
    totalSelectedFilters = 0,
  } = useSearchContextState()

  useEffect(() => {
    setHasSmartSearchPrompt(Boolean(readSmartSearchPrompt(brand)))
  }, [brand])

  const isMobile = Boolean(width && width <= breakpoints[0])
  const isSmartSearchExperimentEnabled =
    brand === 'cottages.com' &&
    !optiContext.isGuestApp &&
    optiContext.isExperimentEnabled(
      OPTIMIZELY_FLAGS_ENUM.SEARCH_SMART_AI_SEARCH,
    ) &&
    optiContext.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.SEARCH_SMART_AI_SEARCH,
    ) === 'variant_a'

  const {
    selectedFeatures,
    accommodationTypesAsFeatures,
    popularDestinationIds,
    popularFeatureIds,
    siteFeatures,
  } = useMemo(() => {
    const selectedFilterIds = [
      ...(searchCriteria.accommodationTypes ?? []),
      ...(searchCriteria.features ?? []),
      ...(searchCriteria.siteFeatures ?? []),
    ]
    const aggregationFeatures = aggregations?.features ?? []
    const aggregationSiteFeatures = aggregations?.siteFeatures ?? []
    const accommodationTypesAsFeatures =
      aggregations?.accommodationTypes?.map(({ accommodationId, count }) => ({
        featureCode: accommodationId,
        featureId: accommodationId,
        featureName: formatMessage({
          id: `filters.accommodation-types.${accommodationId}`,
          defaultMessage: accommodationId,
        }),
        count,
      })) ?? []
    const start = searchCriteria.start ?? ''
    const hideFreeCancellation =
      containsYear2027(start) || containsYear2025(start)
    const combinedAndSortedFeatures = processFeaturesAndSiteFeatures(
      [...aggregationFeatures, ...accommodationTypesAsFeatures],
      aggregationSiteFeatures,
      hideFreeCancellation,
    )
    const popularFeatureIds =
      aggregations?.popularFeatures?.map(
        (feature: AggregationFeatures) => feature.featureId,
      ) ?? []
    const popularDestinationIds = getPopularDestinations(
      combinedAndSortedFeatures,
    ).map(({ featureId }) => String(featureId))

    return {
      selectedFeatures: buildSelectedFilters(
        combinedAndSortedFeatures,
        selectedFilterIds,
      ),
      accommodationTypesAsFeatures,
      popularDestinationIds,
      popularFeatureIds,
      siteFeatures: aggregationSiteFeatures,
    }
  }, [aggregations, formatMessage, searchCriteria])

  if (
    !isMobile ||
    !isSmartSearchExperimentEnabled ||
    !hasSmartSearchPrompt ||
    !shouldShowSelectedFilterChips(searchCriteria, totalSelectedFilters)
  ) {
    return null
  }

  return (
    <div className={styles.row} data-testid="smart-search-applied-filters-row">
      <span className={styles.label}>
        {formatMessage({
          id: 'smart-search-applied-filters.label',
          defaultMessage: 'Applied filters:',
        })}
      </span>
      <SelectedFilterChips
        accommodationTypesAsFeatures={accommodationTypesAsFeatures}
        className={styles.chips}
        dispatch={dispatch}
        popularDestinationIds={popularDestinationIds}
        popularFeatureIds={popularFeatureIds}
        searchCriteria={searchCriteria}
        selectedFilters={selectedFeatures}
        siteFeatures={siteFeatures}
        totalFiltersSelectedCount={totalSelectedFilters}
      />
    </div>
  )
}
